import type { ApplicationInsights, SeverityLevel } from "@microsoft/applicationinsights-web";

// SeverityLevel.Warning (2) inlined so the AppInsights SDK is only loaded lazily by the plugin.
const WARNING_SEVERITY = 2 as SeverityLevel;

let appInsights: ApplicationInsights | null = null;

/**
 * Stores the Application Insights instance created by the `vue3-application-insights`
 * plugin so non-component code (e.g. the prod logger, Algolia error reporting) can send
 * telemetry. Invoked from the plugin's `onLoaded` callback.
 */
export const setApplicationInsights = (instance: ApplicationInsights): void => {
  appInsights = instance;
};

/** True when the error is an Algolia HTTP 429 rate-limit response. */
const is429 = (error: unknown): boolean =>
  typeof error === "object" && error !== null && (error as { status?: number }).status === 429;

/**
 * Reports an Algolia HTTP 429 rate-limit error to Application Insights as both an exception
 * and a custom event. Non-429 errors are ignored so they can be handled by existing logic.
 */
export const reportAlgolia429 = (error: unknown, source: string, indexNames?: string[]): void => {
  if (!is429(error)) {
    return;
  }

  const properties = { source, indexNames: indexNames?.join(",") ?? "" };
  appInsights?.trackEvent({ name: "AlgoliaRateLimited" }, properties);
  appInsights?.trackException({
    exception: error instanceof Error ? error : new Error("Algolia 429 rate-limit"),
    severityLevel: WARNING_SEVERITY,
    properties,
  });
};
